import { ArrowRight, FileText } from "lucide-react";
import {
	Button,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Textarea,
} from "@/components/primitives";

interface DescriptionStepProps {
	/**
	 * The reflection title
	 */
	title: string;
	/**
	 * The reflection description
	 */
	description: string;
	/**
	 * Callback when title changes
	 */
	onTitleChange: (value: string) => void;
	/**
	 * Callback when description changes
	 */
	onDescriptionChange: (value: string) => void;
	/**
	 * Callback when user clicks "Save and Continue"
	 */
	onContinue: () => void;
	/**
	 * Callback when user clicks "Save" (without continuing)
	 */
	onSave?: () => void;
	/**
	 * Whether to show loading state
	 */
	isLoading?: boolean;
	/**
	 * Optional subtitle
	 */
	subtitle?: string;
}

export function DescriptionStep({
	title,
	description,
	onTitleChange,
	onDescriptionChange,
	onContinue,
	onSave,
	isLoading = false,
	subtitle,
}: DescriptionStepProps) {
	return (
		<>
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
						<FileText className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Your Reflection Summary
						</CardTitle>
						<CardDescription className="text-sm mt-1">
							{subtitle ||
								"Review and edit your reflection description before continuing."}
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				<div className="space-y-6">
					{/* Title Input */}
					<div>
						<label className="text-sm font-semibold mb-2 block text-foreground">
							Title
						</label>
						<Input
							data-testid="reflection-title-input"
							value={title}
							onChange={(e) => onTitleChange(e.target.value)}
							placeholder="Reflection title"
							disabled={isLoading}
						/>
					</div>

					{/* Description Textarea */}
					<div>
						<label className="text-sm font-semibold mb-2 block text-foreground">
							Description
						</label>
						<Textarea
							data-testid="reflection-description-textarea"
							value={description}
							onChange={(e) => onDescriptionChange(e.target.value)}
							placeholder="Describe your reflection..."
							className="min-h-[150px] text-base"
							disabled={isLoading}
						/>
					</div>

					{/* Action Buttons */}
					<div className="flex justify-end gap-2 pt-2">
						{onSave && (
							<Button
								data-testid="reflection-description-save-button"
								variant="outline"
								onClick={onSave}
								disabled={isLoading || !title.trim() || !description.trim()}
							>
								Save
							</Button>
						)}
						<Button
							data-testid="reflection-description-continue-button"
							onClick={onContinue}
							disabled={isLoading || !title.trim() || !description.trim()}
							className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
						>
							Save and Continue
							<ArrowRight className="h-4 w-4 ml-2" />
						</Button>
					</div>
				</div>
			</CardContent>
		</>
	);
}
